/**
 * Pack 9 · Benchmark Scope Editor
 *
 * Read-only browse + admin CRUD for the `benchmarks` collection with the
 * new `scope_level` + `region_id` fields. Scope semantics:
 *   Country → matches when prospect.country_id equals this row.
 *   Region  → matches when prospect's country belongs to this region.
 *   Global  → cross-region fallback (country_id must be "*").
 *
 * The scope + region selectors here drive `POST /api/taxonomy/admin/benchmarks`;
 * validation mirrors the Pydantic guard on the backend so bad values surface
 * client-side before a round-trip.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Save, RefreshCw, Filter } from "lucide-react";

const SCOPES = [
  { value: "country", label: "Country" },
  { value: "region", label: "Region" },
  { value: "global", label: "Global" },
];

const emptyDraft = {
  kpi_code: "",
  country_id: "",
  industry_id: "*",
  function_id: "*",
  scope_level: "country",
  region_id: "",
  p25: "",
  p50: "",
  p75: "",
  unit: "",
  sample_size: 0,
  confidence: 0.7,
  tier: 6,
  source_id: "admin_manual",
};

function scopeChip(scope) {
  switch ((scope || "").toLowerCase()) {
    case "global":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
    case "region":
      return "bg-amber-500/15 text-amber-300 border-amber-500/40";
    default:
      return "bg-accent/15 text-accent border-accent/40";
  }
}

export default function BenchmarkScopeEditor() {
  const [rows, setRows] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [filter, setFilter] = useState({ scope: "", region_id: "", country_id: "", kpi_code: "" });

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.scope) q.set("scope_level", filter.scope);
      if (filter.region_id) q.set("region_id", filter.region_id);
      if (filter.country_id) q.set("country_id", filter.country_id);
      if (filter.kpi_code) q.set("kpi_code", filter.kpi_code);
      const r = await api.get(`/taxonomy/admin/benchmarks?${q.toString()}`);
      setRows(r.data.benchmarks || []);
    } catch {
      toast.error("Failed to load benchmarks");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadMeta = useCallback(async () => {
    try {
      const [reg, ctr] = await Promise.all([
        api.get("/taxonomy/admin/regions"),
        api.get("/taxonomy/countries?limit=500"),
      ]);
      setRegions(reg.data.regions || []);
      setCountries(ctr.data.countries || []);
    } catch {
      // Non-fatal — the selectors still work with free-text.
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const invalidDraft = useMemo(() => {
    if (!draft.kpi_code) return "kpi_code is required";
    if (!draft.p50 && draft.p50 !== 0) return "p50 is required";
    if (draft.scope_level === "country" && (!draft.country_id || draft.country_id === "*"))
      return "country_id is required when scope is Country";
    if (draft.scope_level === "region" && !draft.region_id)
      return "region_id is required when scope is Region";
    return null;
  }, [draft]);

  const save = async () => {
    if (invalidDraft) return toast.error(invalidDraft);
    const payload = {
      kpi_code: draft.kpi_code.trim(),
      country_id: draft.scope_level === "global" ? "*" : draft.country_id.trim(),
      industry_id: draft.industry_id || "*",
      function_id: draft.function_id || "*",
      scope_level: draft.scope_level,
      region_id: draft.scope_level === "region" ? draft.region_id : "",
      value: {
        ...(draft.p25 !== "" && !Number.isNaN(Number(draft.p25)) ? { p25: Number(draft.p25) } : {}),
        p50: Number(draft.p50),
        ...(draft.p75 !== "" && !Number.isNaN(Number(draft.p75)) ? { p75: Number(draft.p75) } : {}),
      },
      unit: draft.unit || "",
      sample_size: Number(draft.sample_size) || 0,
      confidence: Number(draft.confidence) || 0.7,
      tier: Number(draft.tier) || 6,
      source_id: draft.source_id || "admin_manual",
    };
    try {
      await api.post("/taxonomy/admin/benchmarks", payload);
      toast.success("Benchmark saved");
      setDraft(emptyDraft);
      loadRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="benchmark-scope-editor">
      {/* --- New / update form --- */}
      <Card className="p-5 card-elevated">
        <div className="overline">Add / Update Benchmark Atom</div>
        <div className="grid md:grid-cols-6 gap-3 mt-3">
          <div className="md:col-span-2">
            <Label>KPI Code</Label>
            <Input
              data-testid="bench-kpi-code"
              value={draft.kpi_code}
              onChange={(e) => setDraft({ ...draft, kpi_code: e.target.value })}
              placeholder="close_velocity_days"
            />
          </div>
          <div>
            <Label>Scope</Label>
            <Select
              value={draft.scope_level}
              onValueChange={(v) => setDraft({ ...draft, scope_level: v,
                country_id: v === "global" ? "*" : draft.country_id,
                region_id: v === "region" ? draft.region_id : "" })}
            >
              <SelectTrigger data-testid="bench-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`bench-scope-opt-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Region</Label>
            <Select
              value={draft.region_id || "__none__"}
              onValueChange={(v) => setDraft({ ...draft, region_id: v === "__none__" ? "" : v })}
              disabled={draft.scope_level !== "region"}
            >
              <SelectTrigger data-testid="bench-region-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {regions.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`bench-region-opt-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Country</Label>
            <Input
              data-testid="bench-country"
              value={draft.country_id}
              onChange={(e) => setDraft({ ...draft, country_id: e.target.value })}
              placeholder={draft.scope_level === "global" ? "*" : "SAU"}
              disabled={draft.scope_level === "global"}
            />
          </div>
          <div>
            <Label>Industry</Label>
            <Input
              data-testid="bench-industry"
              value={draft.industry_id}
              onChange={(e) => setDraft({ ...draft, industry_id: e.target.value || "*" })}
            />
          </div>
          <div>
            <Label>Function</Label>
            <Input
              data-testid="bench-function"
              value={draft.function_id}
              onChange={(e) => setDraft({ ...draft, function_id: e.target.value || "*" })}
            />
          </div>
          <div>
            <Label>P25</Label>
            <Input data-testid="bench-p25" value={draft.p25}
                   onChange={(e) => setDraft({ ...draft, p25: e.target.value })} />
          </div>
          <div>
            <Label>P50 *</Label>
            <Input data-testid="bench-p50" value={draft.p50}
                   onChange={(e) => setDraft({ ...draft, p50: e.target.value })} />
          </div>
          <div>
            <Label>P75</Label>
            <Input data-testid="bench-p75" value={draft.p75}
                   onChange={(e) => setDraft({ ...draft, p75: e.target.value })} />
          </div>
          <div>
            <Label>Unit</Label>
            <Input data-testid="bench-unit" value={draft.unit}
                   onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                   placeholder="days" />
          </div>
          <div>
            <Label>Confidence</Label>
            <Input data-testid="bench-confidence" value={draft.confidence}
                   onChange={(e) => setDraft({ ...draft, confidence: e.target.value })}
                   placeholder="0.7" />
          </div>
          <div>
            <Label>Tier</Label>
            <Input data-testid="bench-tier" value={draft.tier}
                   onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
                   placeholder="6" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button data-testid="bench-save" onClick={save} disabled={!!invalidDraft}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
          {invalidDraft && (
            <span className="text-xs text-red-300" data-testid="bench-invalid">{invalidDraft}</span>
          )}
        </div>
      </Card>

      {/* --- Filter row --- */}
      <Card className="p-5 card-elevated">
        <div className="flex items-center justify-between gap-2">
          <div className="overline flex items-center gap-2"><Filter className="w-3.5 h-3.5" /> Filter</div>
          <Button variant="outline" size="sm" onClick={loadRows} data-testid="bench-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        <div className="grid md:grid-cols-4 gap-3 mt-3">
          <div>
            <Label>Scope</Label>
            <Select value={filter.scope || "__all__"}
                    onValueChange={(v) => setFilter({ ...filter, scope: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="bench-filter-scope"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All scopes</SelectItem>
                {SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Region</Label>
            <Select value={filter.region_id || "__all__"}
                    onValueChange={(v) => setFilter({ ...filter, region_id: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="bench-filter-region"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All regions</SelectItem>
                {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Country</Label>
            <Input data-testid="bench-filter-country" value={filter.country_id}
                   onChange={(e) => setFilter({ ...filter, country_id: e.target.value })}
                   placeholder="SAU" />
          </div>
          <div>
            <Label>KPI</Label>
            <Input data-testid="bench-filter-kpi" value={filter.kpi_code}
                   onChange={(e) => setFilter({ ...filter, kpi_code: e.target.value })}
                   placeholder="close_velocity_days" />
          </div>
        </div>
      </Card>

      {/* --- Rows --- */}
      <Card className="p-0 card-elevated overflow-hidden">
        <Table data-testid="bench-rows">
          <TableHeader>
            <TableRow>
              <TableHead>KPI</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Function</TableHead>
              <TableHead>P50</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-6" data-testid="bench-empty">
                  No benchmarks match the current filter.
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((r, i) => (
              <TableRow key={`${r.id || r.source_id}-${i}`} data-testid={`bench-row-${r.kpi_code}-${r.country_id}`}>
                <TableCell className="font-mono">{r.kpi_code}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${scopeChip(r.scope_level)}`}>
                    {r.scope_level || "country"}
                  </span>
                </TableCell>
                <TableCell className="font-mono">{r.country_id}</TableCell>
                <TableCell className="font-mono">{r.region_id || "—"}</TableCell>
                <TableCell className="font-mono">{r.industry_id}</TableCell>
                <TableCell className="font-mono">{r.function_id}</TableCell>
                <TableCell>{r?.value?.p50}</TableCell>
                <TableCell className="text-muted-foreground">{r.unit || "—"}</TableCell>
                <TableCell>{r.confidence?.toFixed?.(2) ?? r.confidence ?? "—"}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{r.source_id}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
